import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Nav, NavItem, NavLink } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import usePermission from '../Hooks/usePermission';
import {
  CATEGORY_READ,
  ASSET_TYPE_READ,
  SPARE_PART_TYPE_READ,
  USER_READ,
  INVITATION_READ,
  ROLE_READ,
  TEAM_READ,
  VENDOR_READ,
  MAINTENANCE_READ,
  ESCALATION_READ,
  CUSTOM_FIELD_PERMISSIONS,
} from '../constants/permissions';

import MyAccount from './MyAccount';
import OrganizationSettings from './OrganizationSettings';
import CategoriesManagement from './CategoriesManagement';
import Contracts from './Contracts';
import UserManagement from './UserManagement';
import Vendor from './Vendor';
import EscalationSettings from './EscalationSettings';
import CustomFieldsManagement from './CustomFieldsManagement';

const Settings = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission, isAdmin, isRequestor } = usePermission();

  const allTabs = useMemo(() => [
    {
      key: 'my-account',
      label: t('shared.my_account', 'My Account'),
      icon: 'user',
      path: '/settings/my-account',
      visible: true,
      component: MyAccount,
    },
    {
      key: 'organization',
      label: t('shared.organization', 'Organization'),
      icon: 'briefcase',
      path: '/settings/organization',
      visible: isAdmin,
      component: OrganizationSettings,
    },
    {
      key: 'categories',
      label: t('shared.categories', 'Categories'),
      icon: 'grid',
      path: '/settings/categories',
      visible: !isRequestor && hasAnyPermission([CATEGORY_READ, ASSET_TYPE_READ, SPARE_PART_TYPE_READ, MAINTENANCE_READ]),
      component: CategoriesManagement,
      matchPaths: ['/settings/categories', '/settings/work-order-categories', '/settings/asset-categories', '/settings/spare-part-types', '/settings/contract-categories'],
    },
    {
      key: 'custom-fields',
      label: t('shared.custom_fields', 'Custom Fields'),
      icon: 'list',
      path: '/settings/custom-fields',
      visible: !isRequestor && hasAnyPermission(CUSTOM_FIELD_PERMISSIONS),
      component: CustomFieldsManagement,
      matchPaths: ['/settings/custom-fields', '/settings/asset-custom-fields'],
    },
    {
      key: 'contracts',
      label: t('shared.contracts', 'Contracts'),
      icon: 'file-text',
      path: '/settings/contracts',
      visible: !isRequestor && hasPermission(MAINTENANCE_READ),
      component: Contracts,
    },
    {
      key: 'vendors',
      label: t('shared.vendors', 'Vendors'),
      icon: 'shopping-bag',
      path: '/settings/vendors',
      visible: !isRequestor && hasPermission(VENDOR_READ),
      component: Vendor,
    },
    {
      key: 'users',
      label: t('shared.user_management', 'User Management'),
      icon: 'users',
      path: '/settings/users',
      visible: !isRequestor && hasAnyPermission([USER_READ, INVITATION_READ, ROLE_READ, TEAM_READ]),
      component: UserManagement,
      matchPaths: ['/settings/users', '/settings/teams', '/settings/invitations', '/settings/roles'],
    },
    {
      key: 'escalations',
      label: t('escalation.title', 'Escalations'),
      icon: 'alert-triangle',
      path: '/settings/escalations',
      visible: isAdmin || hasPermission(ESCALATION_READ),
      component: EscalationSettings,
    },
  ], [t, isAdmin, isRequestor, hasPermission, hasAnyPermission]);

  const visibleTabs = useMemo(() => allTabs.filter((tab) => tab.visible), [allTabs]);

  const activeTab = useMemo(() => {
    const path = location.pathname;
    const matchedByExtra = visibleTabs.find(
      (tab) => tab.matchPaths && tab.matchPaths.includes(path)
    );
    if (matchedByExtra) return matchedByExtra;
    return visibleTabs.find((tab) => tab.path === path);
  }, [location.pathname, visibleTabs]);

  useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      navigate(visibleTabs[0].path, { replace: true });
    }
  }, [activeTab, visibleTabs, navigate]);

  useEffect(() => {
    document.title = `${t('shared.settings', 'Settings')} | Arcmedix`;
  }, [t]);

  if (!activeTab) return null;

  const ActiveComponent = activeTab.component;

  return (
    <>
      <div className="page-content" style={{ paddingBottom: 0 }}>
        <div className="container-fluid">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3"
          >
            <h4 className="mb-0 fw-bold">{t('shared.settings', 'Settings')}</h4>
          </motion.div>

          <Nav tabs className="settings-tabs mb-4 border-bottom">
            {visibleTabs.map((tab) => (
              <NavItem key={tab.key} className="flex-shrink-0">
                <NavLink
                  className={classnames({ active: activeTab.key === tab.key })}
                  onClick={() => navigate(tab.path)}
                  style={{ cursor: 'pointer' }}
                >
                  <FeatherIcon icon={tab.icon} size={16} className="me-2" />
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
      </div>

      <div className="settings-tab-content">
        <ActiveComponent />
      </div>

      <style>{`
        .settings-tabs {
          gap: 0.5rem;
          flex-wrap: nowrap;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .settings-tabs::-webkit-scrollbar {
          display: none;
        }
        .settings-tabs .nav-link {
          color: #6c757d;
          background-color: #fff;
          white-space: nowrap;
        }
        .settings-tabs .nav-link.active {
          color: #3980c0;
          background-color: #fff;
          border-bottom: 2px solid #3980c0;
        }
        .settings-tab-content .page-content {
          padding-top: 0 !important;
        }
      `}</style>
    </>
  );
};

export default Settings;
